/** Suggested review-check calibration fixes from Ace stability test variance. */

import type {
  AceStabilityCheckVariance,
  AceStabilityPhaseVariance,
  AceStabilityTestResult,
} from "@/services/api/agentReview";

export interface StabilityCalibrationSuggestion {
  kind: "check" | "phase";
  phase: string;
  title: string;
  checkId?: string;
  severity?: string;
  observation: string;
  editLocation: string;
  cursorFileRef: string;
  suggestion: string;
}

function phaseLabel(phase: string): string {
  if (phase === "orchestration") return "Orchestration";
  if (phase === "artefact") return "Artefact";
  return "Output";
}

export { phaseLabel };

function isArtefactCheckId(checkId: string): boolean {
  return /\.art\./.test(checkId);
}

function isGenericCheck(checkId: string): boolean {
  return checkId.startsWith("generic.");
}

function genericCriteriaList(checkId: string): string {
  return checkId.includes(".orch.") ? "_GENERIC_ORCHESTRATION" : "_GENERIC_OUTPUT";
}

const CRITERIA_PATH = "ani_api/app/core/agentic_v2/services/agent_review/criteria.py";
const GUIDANCE_PATH = "ani_api/app/core/agentic_v2/services/agent_review/guidance.py";
const ARTEFACTS_PATH = "ani_api/app/core/agentic_v2/services/agent_review/artefacts.py";

export function checkEditLocation(
  checkId: string,
  catalogueKey?: string | null,
): { path: string; detail: string; cursorFileRef: string } {
  if (isArtefactCheckId(checkId)) {
    return {
      path: ARTEFACTS_PATH,
      detail: `artefact review criteria (e.g. CHEAT_SHEET_ARTEFACT_REVIEW_CRITERIA) → id \`${checkId}\``,
      cursorFileRef: `@${ARTEFACTS_PATH}`,
    };
  }

  if (isGenericCheck(checkId)) {
    const list = genericCriteriaList(checkId);
    return {
      path: CRITERIA_PATH,
      detail: `${list} → id \`${checkId}\``,
      cursorFileRef: `@${CRITERIA_PATH}`,
    };
  }

  const catalogueHint = catalogueKey ? `catalogue \`${catalogueKey}\`` : "this task type";
  return {
    path: GUIDANCE_PATH,
    detail: `REVIEW_GUIDANCE_BY_CATALOGUE_KEY for ${catalogueHint} (or seed \`human_interaction.review_guidance\`) → id \`${checkId}\``,
    cursorFileRef: `@${GUIDANCE_PATH}`,
  };
}

export function answerDistributionSummary(check: AceStabilityCheckVariance): string {
  const parts: string[] = [];
  if (check.pass_count) parts.push(`Pass ${check.pass_count}`);
  if (check.fail_count) parts.push(`Fail ${check.fail_count}`);
  if (check.na_count) parts.push(`N/A ${check.na_count}`);
  if (check.other_count) parts.push(`Other ${check.other_count}`);
  const summary = parts.join(", ");
  return check.mode_answer ? `${summary} (mode: ${check.mode_answer})` : summary;
}

function suggestCheckFix(check: AceStabilityCheckVariance): string {
  const hasPass = check.pass_count > 0;
  const hasFail = check.fail_count > 0;
  const hasNa = check.na_count > 0;

  if (hasPass && hasFail && hasNa) {
    return (
      "Answers are spread across pass, fail, and N/A. Rewrite help_text as a short decision tree: " +
      "when N/A applies, what clearly fails, and what clearly passes."
    );
  }
  if (hasPass && hasFail) {
    return (
      "Ace disagrees with itself on pass vs fail. Add concrete pass/fail examples to help_text — " +
      "spell out the boundary on this check."
    );
  }
  if (hasPass && hasNa) {
    return (
      "Ace sometimes marks N/A when it should pass (or vice versa). Clarify when N/A applies in help_text."
    );
  }
  if (hasFail && hasNa) {
    return (
      "Ace is split between fail and N/A. Clarify applicability: when is a failure required vs when should the reviewer skip?"
    );
  }
  if (check.severity === "soft") {
    return (
      "Soft check with multiple answers — tighten wording or consider whether this should be hard if variance affects RAG."
    );
  }
  return "Tighten label and help_text so Ace applies the same standard on every run.";
}

function suggestPhaseFix(phase: AceStabilityPhaseVariance): string {
  return (
    "Suggested RAG varied across identical runs. Review the phase question and intro in review guidance, " +
    "and whether soft-check disagreement is flipping the phase RAG."
  );
}

function phaseEditLocation(phase: string, catalogueKey?: string | null): {
  path: string;
  detail: string;
  cursorFileRef: string;
} {
  if (phase === "artefact") {
    return {
      path: ARTEFACTS_PATH,
      detail: "artefact review criteria question / intro for this task type",
      cursorFileRef: `@${ARTEFACTS_PATH}`,
    };
  }
  const catalogueHint = catalogueKey ? `catalogue \`${catalogueKey}\`` : "this task type";
  return {
    path: GUIDANCE_PATH,
    detail: `phase question / intro for ${catalogueHint}`,
    cursorFileRef: `@${GUIDANCE_PATH}`,
  };
}

export function buildStabilityCalibrationSuggestions(
  result: AceStabilityTestResult,
  catalogueKey?: string | null,
): StabilityCalibrationSuggestion[] {
  if (result.iterations_completed < result.iterations_requested) {
    return [];
  }

  const suggestions: StabilityCalibrationSuggestion[] = [];

  for (const check of result.checks.filter((row) => !row.stable)) {
    const location = checkEditLocation(check.check_id, catalogueKey);
    suggestions.push({
      kind: "check",
      phase: check.phase,
      title: check.check_label,
      checkId: check.check_id,
      severity: check.severity,
      observation: answerDistributionSummary(check),
      editLocation: `${location.path} → ${location.detail}`,
      cursorFileRef: location.cursorFileRef,
      suggestion: suggestCheckFix(check),
    });
  }

  for (const phase of result.phases.filter((row) => !row.stable)) {
    const distribution = Object.entries(phase.rag_counts)
      .sort((a, b) => b[1] - a[1])
      .map(([rag, count]) => `${rag}: ${count}`)
      .join(", ");
    const location = phaseEditLocation(phase.phase, catalogueKey);
    suggestions.push({
      kind: "phase",
      phase: phase.phase,
      title: phase.artifact_title
        ? `${phaseLabel(phase.phase)} — ${phase.artifact_title}`
        : `${phaseLabel(phase.phase)} phase RAG`,
      observation: distribution,
      editLocation: `${location.path} → ${location.detail}`,
      cursorFileRef: location.cursorFileRef,
      suggestion: suggestPhaseFix(phase),
    });
  }

  return suggestions;
}

export function compileStabilityCalibrationPrompt(
  result: AceStabilityTestResult,
  catalogueKey?: string | null,
): string {
  if (result.stable) return "";
  if (result.iterations_completed < result.iterations_requested) return "";

  const suggestions = buildStabilityCalibrationSuggestions(result, catalogueKey);
  const fileRefs = [...new Set(suggestions.map((item) => item.cursorFileRef))];
  const checkSuggestions = suggestions.filter((item) => item.kind === "check");
  const phaseSuggestions = suggestions.filter((item) => item.kind === "phase");

  const lines: string[] = [
    "Calibrate Ace review checks so repeated runs on the same case return consistent answers.",
    "",
    "## Context",
    `- Case: ${result.subject_label} (run_id=${result.run_id})`,
    ...(catalogueKey ? [`- Task type: ${catalogueKey}`] : []),
    `- Ace profile: ${result.ace_profile}`,
    `- Runs: ${result.iterations_completed}/${result.iterations_requested}`,
    `- Unstable checks: ${result.unstable_check_count}; unstable phases: ${result.unstable_phase_count}`,
    "",
    "Ace returned different answers on the same case. Tighten the review check definitions below.",
    "",
  ];

  if (checkSuggestions.length) {
    lines.push("## Unstable checks");
    lines.push("");
    checkSuggestions.forEach((item, index) => {
      lines.push(`### ${index + 1}. [${phaseLabel(item.phase)}] ${item.title}`);
      if (item.checkId) {
        lines.push(`- Check id: ${item.checkId}${item.severity ? ` (${item.severity})` : ""}`);
      }
      lines.push(`- Observed: ${item.observation}`);
      lines.push(`- Edit: ${item.editLocation}`);
      lines.push(`- Fix: ${item.suggestion}`);
      lines.push("");
    });
  }

  if (phaseSuggestions.length) {
    lines.push("## Unstable phase RAGs");
    lines.push("");
    phaseSuggestions.forEach((item) => {
      lines.push(`### ${phaseLabel(item.phase)}`);
      lines.push(`- Distribution: ${item.observation}`);
      lines.push(`- Edit: ${item.editLocation}`);
      lines.push(`- Fix: ${item.suggestion}`);
      lines.push("");
    });
  }

  lines.push("## What to do");
  lines.push(
    `1. Open ${fileRefs.join(" and ")} and update help_text (and label if needed) for each unstable check.`,
  );
  lines.push("2. For phase RAG variance, review the phase question and intro in the same guidance block.");
  lines.push(
    `3. Re-run the Ace stability test on case "${result.subject_label}" to confirm answers converge.`,
  );

  return lines.join("\n");
}
